import { maxBy } from 'lodash-es'

import {
  COMMUNITY_COURSE,
  GENERAL_COURSE,
  SAT_BOOTCAMP_MATH_COURSE,
  SAT_BOOTCAMP_READING_COURSE,
  WORLD_LEVELS,
  type WorldLevel,
} from '@/queries/constants'
import type { UserContextType } from '@/client/context/internal'
import { hasUserRole } from '@/client/services/user'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'

import type { PostEventDetails } from '@/components/Community/Post/types'
import type { ChannelBase, WorldDetails } from '@/components/Worlds/types'

import {
  CERTIFICATION_WORLD_SLUGS,
  INVITE_ONLY_WORLD_TYPES,
  MEMBER_POSTS_PER_WEEK_LIMIT,
  PROGRAM_SUBWORLD_SLUGS,
  SAT_BOOTCAMP_WORLD_SLUG,
  VISIBLE_WORLD_TYPES,
} from './constants'
import type { ProgramSubworldSlug, WorldVisibility } from './types'

export function getWorldLevelsAboveAndIncluding(level: WorldLevel): WorldLevel[] {
  return WORLD_LEVELS.filter(item => WORLD_LEVELS.indexOf(item) >= WORLD_LEVELS.indexOf(level))
}

export function getMaxWorldLevel(user: UserContextType): WorldLevel {
  const x = maxBy(
    Object.values(user.worlds).map(details => details.level ?? 'member'),
    item => WORLD_LEVELS.indexOf(item)
  )
  return x ?? 'member'
}

export function canCreateNewPosts(user: UserContextType, countPostsInLastWeek: number): boolean {
  const maxLevel = getMaxWorldLevel(user)

  if (maxLevel === 'member') {
    return countPostsInLastWeek < MEMBER_POSTS_PER_WEEK_LIMIT
  }

  return true
}

export function canNotifyUsersInGlobalChannel({ user }: { user: UserContextType }): boolean {
  return hasUserAuthScope(user, 'admin:posts:announcements:create')
}

export function canEmailUsersInGlobalChannel({ user }: { user: UserContextType }): boolean {
  return hasUserRole(user, 'admin:super')
}

export function getPostEventMentionedUserIds({
  payload,
}: {
  payload: PostEventDetails['payload']
}): number[] {
  return payload?.type === 'comment' ? (payload?.mentionedUserIds ?? []) : []
}

export function canEmailUsersInChannel({
  user,
  channel,
}: {
  user: UserContextType
  channel: Pick<ChannelBase, 'type'>
}): boolean {
  return hasUserRole(user, 'admin:super') && channel.type !== 'question'
}

export function worldHasCertification(world: Pick<WorldDetails, 'slug'>): boolean {
  return CERTIFICATION_WORLD_SLUGS.includes(world.slug)
}

export function canHostCourseInWorld({
  courseName,
  world,
  includeCommunityForSATBootcampSubworld = false,
}: {
  courseName: string
  world: Pick<WorldDetails, 'courseName' | 'slug'>
  includeCommunityForSATBootcampSubworld?: boolean
}): boolean {
  const matchesWorldCourse =
    world.slug === SAT_BOOTCAMP_WORLD_SLUG
      ? courseName === SAT_BOOTCAMP_MATH_COURSE ||
        courseName === SAT_BOOTCAMP_READING_COURSE ||
        (includeCommunityForSATBootcampSubworld && courseName === COMMUNITY_COURSE)
      : courseName === world.courseName
  return matchesWorldCourse || courseName === GENERAL_COURSE
}

export function isWorldInviteOnly(visibility: WorldVisibility): boolean {
  return INVITE_ONLY_WORLD_TYPES.includes(visibility)
}

export function isWorldVisible(visibility: WorldVisibility): boolean {
  return VISIBLE_WORLD_TYPES.includes(visibility)
}

export function isProgramWorld(worldSlug: string): worldSlug is ProgramSubworldSlug {
  return PROGRAM_SUBWORLD_SLUGS.some(slug => slug === worldSlug)
}

export function getProgramWorldSlug(worldSlug: string): ProgramSubworldSlug | null {
  return PROGRAM_SUBWORLD_SLUGS.some(slug => slug === worldSlug)
    ? (worldSlug as ProgramSubworldSlug)
    : null
}
